var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { BaseWallet } from '@portkey/types';
import { isNullish } from '@portkey/validator';
export class Wallet extends BaseWallet {
    constructor(accountProvider, storage) {
        super(accountProvider, storage);
        this._addressMap = new Map();
        this._defaultKeyName = 'portkey_sdk_wallet';
    }
    create(numberOfAccounts) {
        for (let i = 0; i < numberOfAccounts; i++) {
            this.add(this._accountProvider.create());
        }
        return this;
    }
    add(account) {
        var _a;
        if (typeof account === 'string') {
            return this.add(this._accountProvider.privateKeyToAccount(account));
        }
        let index = this.length;
        const existAccount = this.get(account.address);
        if (existAccount) {
            console.warn(`Account ${account.address.toLowerCase()} already exists.`);
            index = (_a = this._addressMap.get(account.address.toLowerCase())) !== null && _a !== void 0 ? _a : index;
        }
        this._addressMap.set(account.address.toLowerCase(), index);
        this[index] = account;
        return this;
    }
    get(addressOrIndex) {
        if (typeof addressOrIndex === 'string') {
            const index = this._addressMap.get(addressOrIndex.toLowerCase());
            if (!isNullish(index)) {
                return this[index];
            }
            return undefined;
        }
        return this[addressOrIndex];
    }
    remove(addressOrIndex) {
        let index;
        if (typeof addressOrIndex === 'string') {
            const mapIndex = this._addressMap.get(addressOrIndex.toLowerCase());
            if (isNullish(mapIndex)) {
                return false;
            }
            index = mapIndex;
        }
        else {
            index = addressOrIndex;
        }
        if (this[index]) {
            this.splice(index, 1);
            this._addressMap.clear();
            this.forEach((account, index) => {
                this._addressMap.set(account.address.toLowerCase(), index);
            });
            return true;
        }
        return false;
    }
    clear() {
        this._addressMap.clear();
        // Setting length clears the Array in JS.
        this.length = 0;
        return this;
    }
    encrypt(password, options) {
        return __awaiter(this, void 0, void 0, function* () {
            return Promise.all(this.map((account) => __awaiter(this, void 0, void 0, function* () { return account.encrypt(password, options); })));
        });
    }
    decrypt(encryptedWallets, password, options) {
        return __awaiter(this, void 0, void 0, function* () {
            const results = yield Promise.all(encryptedWallets.map((wallet) => __awaiter(this, void 0, void 0, function* () { return this._accountProvider.decrypt(wallet, password, options); })));
            for (const res of results) {
                this.add(res);
            }
            return this;
        });
    }
    /**
     * Stores the wallet encrypted and as string in storage.
     *
     * @param password - The password to encrypt the wallet
     * @param keyName - (optional) The key used for the storage position, defaults to `"portkey-sdk_wallet"`.
     * @returns Will return boolean value true if saved properly
     */
    save(password, keyName) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this._storage)
                throw new Error('Please set storage first');
            yield this._storage.setItem(keyName !== null && keyName !== void 0 ? keyName : this._defaultKeyName, JSON.stringify(yield this.encrypt(password)));
            return true;
        });
    }
    /**
     * Loads a wallet from storage and decrypts it.
     *
     * @param password - The password to decrypt the wallet.
     * @param keyName - (optional)The key used for storage position, defaults to `portkey-sdk_wallet"`
     * @returns Returns the wallet object
     */
    load(password, keyName) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this._storage)
                throw new Error('Please set storage first');
            const keystore = yield this._storage.getItem(keyName !== null && keyName !== void 0 ? keyName : this._defaultKeyName);
            if (keystore) {
                yield this.decrypt(JSON.parse(keystore) || [], password);
            }
            return this;
        });
    }
}
